import { consola } from 'consola'
import { ref, readonly } from 'vue'
import { useAppUpdateBanner } from './useAppUpdateBanner'
import { useVersion } from '@shared/config'

interface VersionInfo {
  version: string
  buildTime: string
  buildTimestamp: number
  gitCommit: string
  buildNumber: string
  environment: string
  formattedBuildTime: string
}

/**
 * Версия, о которой гостю уже сказали всплывашкой в этом запуске приложения.
 *
 * Ref на уровне модуля намеренно: проверка вызывается на старте и на КАЖДЫЙ `focus`
 * окна, а всплывашка про одно и то же обновление нужна ровно один раз — дальше
 * состояние держит баннер. Внутри composable флаг обнулялся бы вместе с ним.
 */
const announcedVersion = ref<string | null>(null)

/** Только для тестов: сбрасывает «уже рассказали» между кейсами. */
export function __resetAppUpdateAnnouncementsForTests(): void {
  announcedVersion.value = null
}

/**
 * Composable для проверки обновлений приложения
 */
export const useAppUpdate = () => {
  const isChecking = ref(false)
  const hasUpdate = ref(false)
  const latestVersion = ref<string>('')
  const currentVersion = useVersion()
  const updateBanner = useAppUpdateBanner()

  // Флаг берём из runtimeConfig — как в плагине app-update-check. Раньше читали только
  // import.meta.env: в клиентской сборке переменной там может не быть, и проверка молча
  // отключалась при включённом флаге. Значение снимаем в setup — checkForUpdates
  // вызывается ещё и из обработчика focus, где Nuxt-контекста уже нет.
  const runtimeUpdateCheckFlag = (() => {
    try {
      return String(useRuntimeConfig().public.appUpdateCheck ?? '')
    } catch {
      return ''
    }
  })()

  /**
   * Проверяет наличие обновлений.
   *
   * @param options.notify Показывать тосты «обновлений нет» и «не удалось проверить».
   *   По умолчанию выключено: проверка вызывается плагином на старте и на каждый
   *   `focus` окна, и тост на каждое переключение вкладки — это спам. Результат
   *   фоновой проверки виден в баннере. Включать только для явного действия гостя
   *   («Проверить обновления» в настройках), когда ответ ждут.
   */
  const checkForUpdates = async ({ notify = false }: { notify?: boolean } = {}) => {
    const enabled = (runtimeUpdateCheckFlag || String(import.meta.env.NUXT_PUBLIC_APP_UPDATE_CHECK || '')).toLowerCase()
    const isEnabled = enabled === '1' || enabled === 'true' || enabled === 'yes'
    if (!isEnabled) return

    if (isChecking.value) return

    isChecking.value = true
    
    try {
      // Определяем платформу
      const platform = process.client && (window as any).Capacitor?.getPlatform?.() || 'web'
      
      // Получаем информацию о последней версии через новый API
      const { data } = await $fetch<{ 
        success: boolean; 
        data: {
          hasUpdate: boolean;
          forceUpdate: boolean;
          latestVersion: VersionInfo | null;
          currentVersion: {
            version: string;
            buildNumber: string;
          };
        }
      }>('/api/v1/app/version', {
        method: 'GET',
        params: {
          platform,
          version: currentVersion.version,
          buildNumber: currentVersion.buildNumber,
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      })

      if (data && data.hasUpdate && data.latestVersion) {
        const version = data.latestVersion.version
        hasUpdate.value = true
        latestVersion.value = version

        // Об обновлении говорим верхним баннером — так же, как о закрытом заведении.
        // Баннер идемпотентен и держится до самого обновления, поэтому его ставим
        // на каждой проверке, а всплывашки — только на первой (см. announcedVersion).
        updateBanner.setUpdateAvailable(version, Boolean(data.forceUpdate))

        if (announcedVersion.value !== version) {
          announcedVersion.value = version

          // Принудительное обновление дополнительно показываем блокирующим алертом
          if (data.forceUpdate) showForceUpdateNotification(data.latestVersion)
          else showUpdateAvailableNotification(version)
        }
      } else {
        hasUpdate.value = false
        latestVersion.value = ''
        // Версия догнала последнюю (гость обновился) — значит про следующую
        // можно будет сказать заново.
        announcedVersion.value = null
        updateBanner.clearUpdate()
        if (notify) showNoUpdateNotification()
      }
    } catch (error) {
      consola.warn('Failed to check for updates:', error)
      if (notify) showErrorNotification()
    } finally {
      isChecking.value = false
    }
  }

  /**
   * Сравнивает две версии
   * @returns 1 если version1 > version2, -1 если version1 < version2, 0 если равны
   */
  const _compareVersions = (version1: string, version2: string): number => {
    const v1Parts = version1.split('.').map(Number)
    const v2Parts = version2.split('.').map(Number)
    
    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1 = v1Parts[i] || 0
      const v2 = v2Parts[i] || 0
      
      if (v1 > v2) return 1
      if (v1 < v2) return -1
    }
    
    return 0
  }

  /**
   * Показывает уведомление о принудительном обновлении
   */
  const showForceUpdateNotification = async (latestVersion: VersionInfo) => {
    try {
      const { useAlertStore } = await import('@shared/model')
      const alertStore = useAlertStore()
      
      const message = latestVersion.changelog 
        ? `Требуется обновление до версии ${latestVersion.version}.\n\nЧто нового:\n${latestVersion.changelog}`
        : `Требуется обновление приложения до версии ${latestVersion.version}.`
      
      // 0 = тост не гаснет сам: обновление обязательное, а кнопка «Обновить»
      // живёт в баннере — при forceUpdate он ещё и не закрывается.
      alertStore.warn(message, 0)
    } catch (error) {
      consola.warn('Failed to show force update notification:', error)
    }
  }

  /**
   * Одна всплывашка про доступное обновление — на первой проверке, где оно нашлось.
   * Дальше про него напоминает только баннер, до самого обновления.
   */
  const showUpdateAvailableNotification = async (version: string) => {
    try {
      const { useAlertStore } = await import('@shared/model')
      const alertStore = useAlertStore()

      alertStore.info(`Доступна новая версия ${version}. Обновите приложение.`, 5000)
    } catch (error) {
      consola.warn('Failed to show update available notification:', error)
    }
  }

  /**
   * Показывает уведомление об отсутствии обновлений
   */
  const showNoUpdateNotification = async () => {
    try {
      const { useAlertStore } = await import('@shared/model')
      const alertStore = useAlertStore()
      
      alertStore.success('У вас установлена последняя версия приложения!', 3000)
    } catch (error) {
      consola.warn('Failed to show no update notification:', error)
    }
  }

  /**
   * Показывает уведомление об ошибке
   */
  const showErrorNotification = async () => {
    try {
      const { useAlertStore } = await import('@shared/model')
      const alertStore = useAlertStore()
      
      alertStore.error('Не удалось проверить обновления. Попробуйте позже.', 5000)
    } catch (error) {
      consola.warn('Failed to show error notification:', error)
    }
  }

  /**
   * Скачивает обновление
   */
  const downloadUpdate = (latestVersion: VersionInfo) => {
    try {
      const platform = process.client && (window as any).Capacitor?.getPlatform?.() || 'web'
      
      if (platform === 'web') {
        // Для веб-версии просто перезагружаем страницу
        window.location.reload()
      } else {
        // Для нативных платформ скачиваем файл
        const downloadUrl = `/api/v1/app/download/${platform}/${latestVersion.version}`
        window.open(downloadUrl, '_blank')
      }
    } catch (error) {
      consola.error('Failed to download update:', error)
    }
  }

  /**
   * Принудительно обновляет приложение
   */
  const forceUpdate = () => {
    window.location.reload()
  }

  return {
    isChecking: readonly(isChecking),
    hasUpdate: readonly(hasUpdate),
    latestVersion: readonly(latestVersion),
    currentVersion: readonly(currentVersion),
    checkForUpdates,
    // Раньше её дёргала кнопка внутри тоста, но тостер UI-kit таких кнопок не
    // поддерживает — действие переехало в баннер, а сюда осталось точкой входа.
    downloadUpdate,
    forceUpdate,
  }
}
